import {
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select,
  VStack,
} from '@chakra-ui/react';
import { UserRoleSchema } from '@duke-hq/svc-api-db/schemas/enums/UserRole.schema';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Plus } from 'lucide-react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import capitalize from 'lodash-es/capitalize.js';
import { useForm } from 'react-hook-form';
import { emailRegex, phoneRegex } from '@duke-hq/shared-constants/regexPatterns';
import { UserRole } from '@duke-hq/svc-api-db';

export function AddNewAdminUserModal({ onSubmit }: { onSubmit?: () => void }) {
  const createUser = trpcAdmin.users.create.useMutation();
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    handleSubmit,
    register,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: null,
      password: '',
      role: UserRole.ADMIN,
    },
  });

  const availableRoles = [UserRoleSchema.enum.ADMIN, UserRoleSchema.enum.ASSESSOR];

  return (
    <>
      <Button leftIcon={<Icon as={Plus} />} size="sm" variant="primary" onClick={onOpen}>
        <Trans>Add a new user</Trans>
      </Button>
      <Modal isCentered isOpen={isOpen} onClose={onClose} size={'2xl'}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Create a new user</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <form
              onSubmit={handleSubmit(async (data) => {
                await createUser.mutateAsync({
                  ...data,
                  phone_number: data.phone_number || null,
                  picture_url: null,
                });
                toast({
                  title: t`Created`,
                  description: t`User with role ${data.role} created successfully.`,
                  status: 'success',
                  duration: 7000,
                  isClosable: true,
                });

                onClose();
                onSubmit?.();
              })}
              noValidate
            >
              <VStack spacing={4} align="stretch">
                <HStack spacing={3}>
                  <FormControl isInvalid={!!errors.first_name} isRequired>
                    <FormLabel>
                      <Trans>First Name</Trans>
                    </FormLabel>
                    <Input
                      autoFocus
                      autoComplete="off"
                      type="text"
                      {...register('first_name', {
                        required: t`First name is required`,
                        onChange: () => {
                          if (errors.first_name) {
                            clearErrors('first_name');
                          }
                        },
                      })}
                    />
                    <FormErrorMessage>{errors.first_name?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.last_name} isRequired>
                    <FormLabel>
                      <Trans>Last Name</Trans>
                    </FormLabel>
                    <Input
                      autoComplete="off"
                      type="text"
                      {...register('last_name', {
                        required: t`Last name is required`,
                        onChange: () => {
                          if (errors.last_name) {
                            clearErrors('last_name');
                          }
                        },
                      })}
                    />
                    <FormErrorMessage>{errors.last_name?.message}</FormErrorMessage>
                  </FormControl>
                </HStack>

                <FormControl isInvalid={!!errors.email} isRequired>
                  <FormLabel>
                    <Trans>Email</Trans>
                  </FormLabel>
                  <Input
                    autoComplete="off"
                    type="email"
                    {...register('email', {
                      required: t`Email is required`,
                      pattern: {
                        value: emailRegex,
                        message: t`Please enter a valid email address`,
                      },
                      onChange: () => {
                        if (errors.email) {
                          clearErrors('email');
                        }
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.phone_number}>
                  <FormLabel>
                    <Trans>Phone Number</Trans>
                  </FormLabel>
                  <Input
                    autoComplete="off"
                    spellCheck="false"
                    type="tel"
                    {...register('phone_number', {
                      pattern: {
                        value: phoneRegex,
                        message: t`Please enter a valid phone number`,
                      },
                      setValueAs: (value: string | null) => value?.replace(/\s/g, '') ?? null,
                    })}
                  />
                  <FormErrorMessage>{errors.phone_number?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.role} isRequired>
                  <FormLabel>
                    <Trans>Role</Trans>
                  </FormLabel>
                  <Select
                    defaultValue={'USER'}
                    {...register('role', {
                      required: t`Role is required`,
                      onChange: () => {
                        if (errors.role) {
                          clearErrors('role');
                        }
                      },
                    })}
                  >
                    {availableRoles.map((role) => {
                      return (
                        <option key={role} value={role}>
                          {capitalize(role)}
                        </option>
                      );
                    })}
                  </Select>
                  <FormErrorMessage>{errors.role?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.password} isRequired>
                  <FormLabel>
                    <Trans>Password</Trans>
                  </FormLabel>
                  <Input
                    autoComplete="off"
                    type="password"
                    {...register('password', {
                      required: t`Password is required`,
                      onChange: () => {
                        if (errors.role) {
                          clearErrors('password');
                        }
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                </FormControl>

                <Flex justify={'flex-end'}>
                  <Button right={0} type="submit">
                    <Trans>Create</Trans>
                  </Button>
                </Flex>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
